// src/pages/ParentProfile.js
// v3 - 2025-11-13 (restyled, no inline styles)

import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import "../styles/ParentProfile.css";

const API_BASE_URL = "https://ghrianghraffy.com";

const localPart = (email) =>
  email && email.includes("@") ? email.split("@")[0] : "";

const fetchUserProfile = async (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const response = await axios.get(`${API_BASE_URL}/auth/profile`, { headers });
  return response.data;
};

const ParentProfile = () => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editing state (mirrors StaffDashboard)
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const data = await fetchUserProfile(token);
        setProfile(data);
        setNewDisplayName(data.display_name || "");
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleUpdateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setError("Display name cannot be empty.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/auth/update-display-name`,
        { new_display_name: newDisplayName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile((prev) => ({ ...prev, display_name: newDisplayName }));
      setEditingDisplayName(false);
      setMessage("Display name updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      const detail =
        err.response?.data?.detail || "Failed to update display name.";
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const showNudge =
    profile?.email &&
    (profile.display_name || "") === localPart(profile.email);

  if (loading) {
    return (
      <div className="parent-profile-container page">
        <div className="parent-profile-card parent-profile-card--loading">
          <p className="parent-profile-meta">Loading your profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-profile-container page">
      <header className="parent-profile-header">
        <h1 className="parent-profile-title">Parent profile</h1>
        <p className="parent-profile-subtitle">
          Manage how your name appears to your school and other parents.
        </p>
      </header>

      {message && <p className="success-text">{message}</p>}
      {error && <p className="error-text">{error}</p>}

      <section className="parent-profile-card">
        <div className="parent-profile-grid">
          {/* Display name row */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Display name</span>
            <div className="parent-profile-value parent-profile-value--stack">
              {editingDisplayName ? (
                <>
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    maxLength={255}
                    className="parent-display-input"
                  />
                  <div className="parent-edit-actions">
                    <button
                      onClick={handleUpdateDisplayName}
                      disabled={saving}
                      className="parent-edit-btn parent-edit-btn--primary"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDisplayName(false);
                        setNewDisplayName(profile.display_name || "");
                        setError("");
                      }}
                      className="parent-edit-btn parent-edit-btn--ghost"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="parent-display-row">
                  <span className="parent-display-text">
                    {profile.display_name || profile.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingDisplayName(true)}
                    className={`parent-edit-link ${
                      showNudge ? "parent-edit-link--nudge" : ""
                    }`}
                    title="Click here to personalise your display name!"
                  >
                    {showNudge
                      ? "Click to change your display name"
                      : "Edit display name"}
                    {showNudge && (
                      <span className="nudge-arrow">
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Email</span>
            <span className="parent-profile-value parent-profile-value--mono">
              {profile.email}
            </span>
          </div>

          {/* Role */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Role</span>
            <span className="parent-profile-value">
              {profile.role || "PARENT"}
            </span>
          </div>

          {/* School */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School</span>
            <span className="parent-profile-value">
              {profile.school_name || "No school assigned"}
            </span>
          </div>

          {/* School hashtag */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School hashtag</span>
            <span className="parent-profile-value parent-profile-value--accent">
              {profile.school_hashtag || "Not set yet"}
            </span>
          </div>

          {/* Principal */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School principal</span>
            <span className="parent-profile-value">
              {profile.principal_name || "Not available"}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentProfile;
